import { OnboardingGuard } from "./components/OnboardingGuard";
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './layouts/AppShell';
import { HomePage } from './pages/HomePage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useApp } from './context';
import { FeatureGuard, ParentGuard, SuperAdminGuard } from './components/guards';
import { TourProvider, TourSpotlight, TourTooltip } from './components/tour';

// ============================================
// LAZY LOADED PAGES (Code Splitting)
// ============================================

// Heavy pages - lazy load for better initial bundle size
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({default:m.AdminDashboard})));
const EvidencePage = lazy(() => import('./pages/EvidencePage').then(m=>({default:m.EvidencePage})));
const ExplorePage = lazy(() => import('./pages/ExplorePage').then(m => ({ default: m.ExplorePage })));
const PracticePage = lazy(() => import('./pages/PracticePage').then(m => ({ default: m.PracticePage })));
const NeuroLabPage = lazy(() => import('./pages/NeuroLabPage').then(m => ({ default: m.NeuroLabPage })));
const StressReactorPage = lazy(() => import('./pages/StressReactorPage').then(m => ({ default: m.StressReactorPage })));
const CopingToolkitPage = lazy(() => import('./pages/CopingToolkitPage').then(m => ({ default: m.CopingToolkitPage })));
const ProgressPage = lazy(() => import('./pages/ProgressPage').then(m => ({ default: m.ProgressPage })));
const ReflectionsPage = lazy(() => import('./pages/ReflectionsPage').then(m => ({ default: m.ReflectionsPage })));
const JournalPage = lazy(() => import('./pages/JournalPage').then(m => ({ default: m.JournalPage })));
const AICoachPage = lazy(() => import('./pages/AICoachPage').then(m => ({ default: m.AICoachPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage').then(m => ({ default: m.FeedbackPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const ComponentShowcase = lazy(() => import('./pages/ComponentShowcase').then(m => ({ default: m.ComponentShowcase })));
const GoalsPage = lazy(() => import('./pages/GoalsPage').then(m => ({ default: m.GoalsPage })));
const ShopPage = lazy(() => import('./pages/ShopPage').then(m => ({ default: m.ShopPage })));
const BrainGymPage = lazy(() => import('./pages/BrainGymPage').then(m => ({ default: m.BrainGymPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const ParentDashboard = lazy(() => import('./pages/ParentDashboard').then(m => ({ default: m.ParentDashboard })));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));

// ============================================
// LOADING FALLBACK
// ============================================

function PageLoader() {
  return (
    <div className="page-loader" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      color: 'var(--color-text-secondary)',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div className="loading-spinner" style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--color-border)',
          borderTop: '3px solid var(--color-accent)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem',
        }} />
        <p>Loading...</p>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ============================================
// ROUTE GUARDS
// ============================================

// Guard component that redirects to onboarding if not complete
function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { state } = useApp();

  if (!state.onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

// ============================================
// APP COMPONENT
// ============================================

export function App() {
  return (
    <ErrorBoundary>
      <TourProvider>
        {/* Tour overlay components */}
        <TourSpotlight />
        <TourTooltip />

        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Onboarding route */}
            <Route
              path="/onboarding"
              element={
                <OnboardingGuard>
                  <OnboardingPage />
                </OnboardingGuard>
              }
            />

            {/* Main app routes with shell - require onboarding */}
            <Route
              path="/"
              element={
                <RequireOnboarding>
                  <AppShell />
                </RequireOnboarding>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="evidence" element={<EvidencePage />} />
              <Route path="practice" element={<PracticePage />} />
              <Route path="explore" element={<ExplorePage />} />
              <Route path="neuro-lab" element={<NeuroLabPage />} />
              <Route path="stress" element={<StressReactorPage />} />
              <Route path="toolkit" element={<CopingToolkitPage />} />
              <Route path="progress" element={<ProgressPage />} />
              <Route path="reflections" element={<ReflectionsPage />} />
              <Route path="journal" element={<JournalPage />} />
              <Route
                path="coach"
                element={
<AICoachPage />
                }
              />
              <Route path="goals" element={<GoalsPage />} />
              <Route path="shop" element={<ShopPage />} />
              <Route
                path="brain-gym"
                element={
<BrainGymPage />
                }
              />
              <Route
                path="analytics"
                element={
<AnalyticsPage />
                }
              />
              <Route
                path="family"
                element={
                  <ParentGuard>
                    <FeatureGuard feature="parent_dashboard">
                      <ParentDashboard />
                    </FeatureGuard>
                  </ParentGuard>
                }
              />
              <Route path="admin" element={<AdminDashboard />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="pricing" element={<PricingPage />} />
            </Route>

            {/* Platform admin route - outside main shell */}
            <Route
              path="/platform-admin"
              element={
                <SuperAdminGuard>
                  <SuperAdminDashboard />
                </SuperAdminGuard>
              }
            />

            {/* Dev/showcase route */}
            <Route path="/showcase" element={<ComponentShowcase />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </TourProvider>
    </ErrorBoundary>
  );
}

